/**
 * The storage contract for narrative (notes) fields — the free text on the Narrative tabs
 * that prints on customer, supplier and internal documents.
 *
 * ⚠️ MIRRORED from `TMSWeb.Common/Constants/NarrativeConstants.cs` in the TextileMan_WebAPI
 * repo. Change one and you must change the other in the same batch of PRs — the server decides
 * what is stored and the editor decides what is sent, so a mismatch here silently changes how
 * narratives render on documents.
 *
 * A narrative column holds ONE of two things, and both the renderer and the editor have to know
 * which:
 *   - legacy PLAIN TEXT, with real line breaks, as every narrative was before rich text; or
 *   - RICH TEXT HTML from the narrative editor, prefixed with `NarrativeRichTextMarker`.
 *
 * The marker is what tells them apart. Guessing from the content is not good enough: a legacy
 * note where someone typed angle brackets is indistinguishable from real markup, and the two
 * need opposite handling.
 */

/** Prefix identifying a stored narrative as rich-text HTML rather than plain text. */
export const NarrativeRichTextMarker = '<!--rt-->';

/**
 * Indent classes are `ql-indent-1` … `ql-indent-N` — the editor's own class name, stored as
 * emitted rather than translated to a house name. See the C# constant for why.
 */
export const NarrativeIndentClassPrefix = 'ql-indent-';

/**
 * How many indent levels are allowed, and how many the stylesheets define. 8 because that is
 * exactly Quill's own indent whitelist — allowing fewer would let the user indent to a level the
 * server's sanitiser then silently strips on save.
 */
export const NarrativeMaxIndentLevel = 8;

/**
 * Font-size classes are `ql-size-small` / `-large` / `-huge`.
 *
 * ⚠️ Quill registers the CLASS attributor as its default `size` format (`'formats/size':
 * SizeClass` in quill.js), not the style one — which is the only reason font size could be added
 * without allowing a `style` attribute through the server's sanitiser. A size-only run of text
 * has no other tag to carry the class, so the editor emits `<span class="ql-size-large">`; that
 * is why `span` is on the allowlist.
 */
export const NarrativeSizeClassPrefix = 'ql-size-';

/**
 * The size names offered, matching Quill's own whitelist in `formats/size.js` and the server's
 * `NarrativeConstants.AllowedSizes`.
 *
 * ⚠️ "Normal" is NOT in here: normal is the ABSENCE of a class, which the toolbar offers as
 * `false`. Adding a fourth name here without adding it server-side would let the user pick a size
 * the sanitiser silently strips on save.
 */
export const NarrativeSizes = ['small', 'large', 'huge'] as const;
